import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useContextualMenu } from '@databases/contextual-menu';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { getFieldType } from '@databases/fields';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import InlineEditWrapper from '@databases/inline-edit/InlineEditWrapper';
import { messages } from '@databases/intl/messages';
import { isApple } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  type Adjacents,
  getDatabaseWithCurrentView,
  selectFocusedItem,
  selectIsEntireFieldSelected,
  selectSelectedEntireFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { calculateRangeFromFocus } from '@databases/redux/cellSelections/utils';
import { getService } from '@databases/service-container';
import { SkeletonItem } from '@databases/skeleton';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import FieldNameContainer from './FieldNameContainer';
import { FieldNameViewContainer } from './FieldNameViewContainer';

const StyledInlineEditWrapper = styled(InlineEditWrapper)`
  > ${FieldNameViewContainer} {
    padding: ${token('space.025')} ${token('space.050', '4px')}
      ${token('space.025', '2px')} ${token('space.050', '4px')};
  }
`;

export interface FieldNameHeaderProps {
  contentId: string;
  field: DatabaseField<DatabaseFieldType>;
  hoverDeleteButton?: boolean;
  isReadonly?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
  showIcon?: boolean;
  actionComp?: React.ReactNode;
  hideText?: boolean;
}

const fieldNameViewButtonContainerStyles = xcss({
  whiteSpace: 'pre',
  overflow: 'hidden',
  display: 'block',
});
const hideTextStyles = xcss({
  position: 'absolute',
  right: 'space.0',
});

const FieldNameHeader = forwardRef<HTMLInputElement, FieldNameHeaderProps>(
  function FieldNameHeader(
    {
      contentId,
      field,
      showIcon,
      isSelected,
      hoverDeleteButton,
      selectedAdjacentCells,
      isReadonly,
      actionComp,
      hideText,
    },
    _ref
  ) {
    const { showPanel } = usePanel();
    const { closeMenu } = useContextualMenu();
    const store = defaultStoreUtils.useStore();
    const selectionsActions = getService('SelectionsActions');
    const { formatMessage } = useIntl();
    const client = useAnalytics();

    const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
      'databases-frontend-visual-refresh-table-m1'
    );

    /**
     * Selection logic from Redux thunks `selectFieldSingle` and `selectFieldMulti`.
     *
     * - Click: Select field and open settings panel (selectFieldSingle path)
     * - Shift: selectFieldMulti(isRangeSelect=true) — range select, adds to existing
     * - Shift+Meta/Ctrl: selectFieldMulti(isRangeSelect=false) — toggle individual field in/out of selection
     */
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (isReadonly) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const isMultiSelectOn = isModifierKeyPressed(e, [
          isApple ? 'metaKey' : 'ctrlKey',
          'shiftKey',
        ]);
        const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);

        if (isVisualRefreshM1Enabled && isRangeSelectOn) {
          blurActiveElement();
          removeTextSelection();

          const state = store.getState();

          if (!isMultiSelectOn) {
            const focusedItem = selectFocusedItem(state, { contentId, type: 'field' });
            const database = getDatabaseWithCurrentView(state, { contentId });
            const fields = database?.fields ?? [];
            const rangeFieldIds = calculateRangeFromFocus(
              fields,
              focusedItem?.fieldId,
              field.id
            );

            selectionsActions.selectFields(rangeFieldIds, { clear: false });
          } else {
            const isSelected = selectIsEntireFieldSelected(state, { fieldId: field.id });

            if (isSelected) {
              const selectedFieldIds = selectSelectedEntireFieldIds(state, { contentId });
              const remaining = selectedFieldIds.filter((id) => id !== field.id);

              if (remaining.length > 0) {
                selectionsActions.selectFields(remaining, { clearSelectedValues: false });
              } else {
                selectionsActions.deselectAll();
              }
            } else {
              selectionsActions.selectFields([field.id], { clear: false });
            }
          }

          selectionsActions.clearSelectionIntent();

          return;
        }

        selectionsActions.selectFields([field.id]);
        selectionsActions.setSelectionIntent({ type: 'softSelection' });

        showPanel('FieldSettingsPanel', { fieldId: field.id });
        closeMenu();

        client.sendDatabaseFieldUpdatedEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
        });
      },
      [
        isReadonly,
        isVisualRefreshM1Enabled,
        store,
        selectionsActions,
        contentId,
        field.id,
        showPanel,
        closeMenu,
        client,
      ]
    );

    const hasNonEmptyFieldName = useMemo(
      () => field.name && field.name !== '',
      [field.name]
    );

    return (
      <FieldNameContainer onClick={handleClick} data-testid="field-name-container">
        {showIcon && (
          <SkeletonItem isButton>
            <FieldTypeIcon
              type={field.type}
              label={getFieldType(field.type).label}
              noTooltip
              jiraField={field.settings.jiraField}
              showSyncMessage
            />
          </SkeletonItem>
        )}
        {/* TODO: we should bring selection related styles into this component to avoid using the inline edit wrapper */}
        <StyledInlineEditWrapper
          hideBorderOnFocus
          isDraggable={false}
          isSelected={isSelected}
          markAsDeleted={hoverDeleteButton}
          selectedAdjacentCells={selectedAdjacentCells}
          ariaLabel={field.name}
        >
          <Flex direction="row" justifyContent="space-between" alignItems="center">
            <SkeletonItem minWidth={80}>
              <FieldNameViewContainer>
                {hasNonEmptyFieldName
                  ? field.name
                  : formatMessage(messages['field.default-field-name'])}
              </FieldNameViewContainer>
            </SkeletonItem>
            <Box xcss={[fieldNameViewButtonContainerStyles, hideText && hideTextStyles]}>
              {actionComp && <SkeletonItem minWidth={80}>{actionComp}</SkeletonItem>}
            </Box>
          </Flex>
        </StyledInlineEditWrapper>
      </FieldNameContainer>
    );
  }
);

export default FieldNameHeader;
